import { useBreakpointValue } from '@genial/utils';
import { useFocusEffect } from '@react-navigation/native';
import { useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../hooks/useRecoilQuery';
import { basketAdvisorListQuery } from '../../../store/batchTicket/selectors';
import { masterStateSelectedAdvisorSelector } from '../../../store/master/selector';
import { BasketAdvisorListDataItem } from '../../BatchTicket/utils/types';

export const useBatchTicketRequests = () => {
  const selectedAdvisor = useRecoilValue(masterStateSelectedAdvisorSelector);
  const skeletonItemCount = useBreakpointValue({ base: 3, sm: 6, lg: 7 });

  const [allBasketRequests, setAllBasketRequests] = useState<BasketAdvisorListDataItem[]>([]);
  const [page, setPage] = useState(1);

  const {
    data: response,
    error,
    loading: isLoading,
    refresher,
  } = useRecoilQuery(basketAdvisorListQuery({ page }));

  const isFirstPage = page === 1;

  const handleEndReached = () => {
    if (!response?.meta?.pagination.hasNext || isLoading || error) {
      return;
    }
    setPage((prev) => prev + 1);
  };

  useEffect(() => {
    if (!response?.data) {
      return;
    }

    if (page === 1) {
      setAllBasketRequests(response.data);
    } else {
      setAllBasketRequests((prev) => [...prev, ...response.data]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response?.data]);

  useFocusEffect(
    useCallback(() => {
      refresher();
    }, [refresher])
  );

  useEffect(() => {
    setAllBasketRequests([]);
    setPage(1);
  }, [selectedAdvisor]);

  return {
    isLoading: isLoading && isFirstPage,
    isLoadingMore: isLoading && !isFirstPage,
    refresher,
    response: { meta: response?.meta, data: allBasketRequests },
    skeletonItemCount,
    error,
    onEndReached: handleEndReached,
  };
};
